import {
  TVChartContainer,
  useTvChart,
} from 'modules/tw-chart/TVChartContainer';
import {
  createOptions,
  useDexTvChartSettings,
  useSwitch,
} from '../../components/Chart/DexChart';
import { ContractInfo } from 'api/types/httpsTypes/contracts';
import { ResolutionString } from 'modules/tw-chart/charting_library';
import { OpenPositionListItem } from 'api/types/httpsTypes/dex-trade';
import Big from 'big.js';
import { useEffect, useMemo, useRef } from 'react';
import { useDrawEntries } from './useDrawEntries';
import { useWebsocket } from 'api/hooks/useWebsocket';
import { solanaResolutionToTimeframe } from '../../components/Chart/datafeed';
import { useTokenChartWebsocket } from 'api/hooks/useTokenChartWebsocket';
import { chainAsset } from 'api/types/httpsTypes/d-wallets';

type Props = {
  contract: ContractInfo;
  position: OpenPositionListItem;
  poolAddress: string | undefined;
};

export function DexPositionChart({ contract, position, poolAddress }: Props) {
  const [chart, onChange] = useTvChart();

  const [chartSettings, setChartSettings] = useDexTvChartSettings({
    interval: '15' as ResolutionString,
    currencyIsUsd: true,
    isPriceShown: true,
  });
  const chartSettingsRef = useRef(chartSettings);

  useEffect(() => {
    chartSettingsRef.current = chartSettings;
  }, [chartSettings]);

  useEffect(() => {
    if (chart) {
      const interval = setInterval(() => {
        if (chart.chart) {
          chart.chart().setResolution(chartSettingsRef.current.interval);

          clearInterval(interval);

          chart
            .chart()
            .onIntervalChanged()
            .subscribe(null, (newInterval) => {
              setChartSettings((prev) => ({
                ...prev,
                interval: newInterval as ResolutionString,
              }));
            });
        }
      }, 100);

      return () => clearInterval(interval);
    }
  }, [chart]);

  useDrawEntries(
    chart,
    position,
    chartSettingsRef.current.interval,
    chartSettings.currencyIsUsd,
    chartSettings.isPriceShown
  );

  const selectedPool =
    contract && contract?.liquidityInfo?.activePools?.length >= 1
      ? contract.liquidityInfo.activePools[0]
      : undefined;

  const chartSubscription = useMemo(
    () => ({
      chain: contract.generalInfo.chain.key,
      poolAddress,
      interval: solanaResolutionToTimeframe(chartSettings.interval),
      currency: chartSettings.currencyIsUsd ? 'usd' : 'quote',
    }),
    [
      contract.generalInfo.chain.key,
      poolAddress,
      chartSettings.interval,
      chartSettings.currencyIsUsd,
    ]
  );

  useSwitch(
    chart,
    () =>
      setChartSettings({
        ...chartSettingsRef.current,
        currencyIsUsd: !chartSettings.currencyIsUsd,
      }),
    chartSettings.currencyIsUsd,
    'USD',
    chainAsset(contract.generalInfo.chain.key)
  );

  useSwitch(
    chart,
    () =>
      setChartSettings({
        ...chartSettingsRef.current,
        isPriceShown: !chartSettings.isPriceShown,
      }),
    chartSettings.isPriceShown,
    'Price',
    'MarketCap'
  );

  useWebsocket('token_chart', chartSubscription);

  useTokenChartWebsocket(
    contract.generalInfo.chain.key,
    poolAddress ?? '',
    solanaResolutionToTimeframe(chartSettings.interval),
    chartSettings.currencyIsUsd ? 'usd' : 'quote',
    chartSettings.isPriceShown,
    contract.generalInfo.totalSupply ?? 0
  );

  const tvSymbol = useMemo(() => {
    const base = `${contract.generalInfo.symbol || 'Token'}:${
      contract.generalInfo.address
    }:${poolAddress}`;
    return `${base}?usd=${chartSettings.currencyIsUsd}&isPrice=${chartSettings.isPriceShown}`;
  }, [
    contract,
    poolAddress,
    chartSettings.currencyIsUsd,
    chartSettings.isPriceShown,
  ]);

  return (
    <TVChartContainer
      className="xxs:h-[450px] md:h-[400px] xxs:rounded-lg"
      optionsFactory={(theme, defaults) =>
        createOptions(
          theme,
          {
            ...defaults,
            interval: chartSettings.interval as ResolutionString,
          },
          position.averageBuyPriceInUsd
            ? Big(position.averageBuyPriceInUsd).toNumber()
            : contract.liquidityInfo.activePools[0]?.priceDollar || undefined,
          contract.generalInfo.chain.key,
          selectedPool?.dex.key,
          chartSettings.currencyIsUsd,
          chartSettings.isPriceShown,
          contract.generalInfo.totalSupply ?? 0
        )
      }
      onWidgetChange={onChange}
      symbol={tvSymbol}
    />
  );
}
